import { mutate } from "swr";
import { execute, ACTIONS } from "../services/Captcha";
import { RELEASE_HEADER, recordServerRelease } from "../errors/staleBuild";
import { readErrorBody } from "../util/apiErrorMessage";
import { IDENTITY_KEY } from "./identityKey";

export class FetchError extends Error {
  constructor(message, status, json) {
    super(message);
    this.status = status;
    this.json = json;
    this.name = "FetchError";
  }
}

export const HTTPMethods = Object.seal(
  Object.freeze({
    GET: "GET",
    POST: "POST",
    PUT: "PUT",
    PATCH: "PATCH",
    DELETE: "DELETE",
  }),
);

export const baseHeaders = {
  Accept: "application/json;charset=utf-8",
  "Content-Type": "application/json;charset=utf-8",
};

const debug = false;

const PERMISSION_DENIED = "PERMISSION_DENIED";
const DEFAULT_ERROR_MESSAGE = "An error occurred while fetching the data";

export const fetchErrorMessage = (err, fallback = "Something went wrong") =>
  err.json?.message ?? err.json?.errors?.[0]?.message ?? fallback;

export default async function fetcher(url, init = {}) {
  const finalInit = {
    method: HTTPMethods.GET,
    ...init,
    headers: {
      ...baseHeaders,
      ...(init?.headers ?? {}),
    },
  };

  if (debug) {
    console.count(url);
  }

  const res = await window.fetch(url, finalInit);
  recordServerRelease(window, res.headers?.get(RELEASE_HEADER));

  if (!res.ok) {
    const { json, message } = await readErrorBody(res);

    if (json.errorCode === PERMISSION_DENIED) {
      mutate(IDENTITY_KEY);
    }
    throw new FetchError(message ?? DEFAULT_ERROR_MESSAGE, res.status, json);
  }

  return res.json();
}

/**
 * Upload a file with progress tracking. Returns { controller, promise }.
 *
 * @param {string}   url        — destination URL
 * @param {File}     file       — file to upload
 * @param {Object}   [options]
 * @param {Object}   [options.headers]    — additional request headers
 * @param {Function} [options.onProgress] — called with percentage (0–100)
 * @param {string}   [options.method]     — HTTP method (default POST)
 */
const jsonBodyOf = (xhr) => {
  try {
    return (xhr.getResponseHeader("Content-Type") ?? "").includes(
      "application/json",
    )
      ? JSON.parse(xhr.responseText)
      : undefined;
  } catch {
    return undefined;
  }
};

export function uploadWithProgress(url, file, options = {}) {
  const { headers = {}, onProgress, method = HTTPMethods.POST } = options;

  const controller = new AbortController();
  const xhr = new XMLHttpRequest();

  controller.signal.addEventListener("abort", () => xhr.abort());

  const promise = new Promise((resolve, reject) => {
    xhr.open(method, url);

    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, String(value));
    });

    if (typeof onProgress === "function") {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      });
    }

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const contentType = xhr.getResponseHeader("Content-Type") ?? "";
        resolve(
          contentType.includes("application/json")
            ? JSON.parse(xhr.responseText)
            : xhr.responseText,
        );
      } else {
        reject(
          new FetchError(
            `Upload failed with status ${xhr.status}`,
            xhr.status,
            jsonBodyOf(xhr),
          ),
        );
      }
    });

    xhr.addEventListener("error", () =>
      reject(new FetchError("Network error during upload", 0)),
    );
    xhr.addEventListener("abort", () =>
      reject(new FetchError("Upload aborted", 0)),
    );

    xhr.send(file);
  });

  return { controller, promise };
}

export function createCaptchaFetcher(action = ACTIONS.ORDER_STATUS) {
  return async function captchaFetcher(url = "", init = {}) {
    const token = await execute(action);

    const [path, searchParams] = url.split("?");

    let params = new URLSearchParams();

    if (searchParams) {
      params = new URLSearchParams(searchParams);
    }

    params.set("ct", token);

    return fetcher(`${path}?${params}`, init);
  };
}
